import { useState, useEffect } from 'react'
import { RefreshCw, Loader2, Wallet, Users, Coins, AlertCircle, ChevronDown, Crown, Zap, Search, X, Download } from 'lucide-react'
import { useTokenHolders, useHoldersSummary, useSyncTokenHolders } from '@/hooks/useTokenHolders'
import { TokenType } from '@/lib/api'
import { shortenAddress } from '@/lib/utils'
import { cn } from '@/lib/utils'

const tabs: { id: TokenType; label: string; icon: any; symbol: string }[] = [
  { id: 'W2USDT', label: 'W2USDT', icon: Coins, symbol: 'W2USDT' },
  { id: 'UNI2_TOKEN', label: 'UNI2 Token', icon: Coins, symbol: 'UNI2' },
  { id: 'UNI2_COIN', label: 'UNI2 Coin', icon: Wallet, symbol: 'UNI2' },
]

export function HoldersPage() {
  const [activeTab, setActiveTab] = useState<TokenType>('W2USDT')
  const [syncModalOpen, setSyncModalOpen] = useState(false)
  const { summary, isLoading: summaryLoading, refetch: refetchSummary } = useHoldersSummary()

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Token Holders</h1>
        <button
          onClick={() => setSyncModalOpen(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-uni-500/20 text-uni-400 hover:bg-uni-500/30 transition-colors text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Sincronizar
        </button>
      </div>

      {/* Sync Modal */}
      {syncModalOpen && (
        <SyncModal
          summary={summary}
          onClose={() => setSyncModalOpen(false)}
          onSyncComplete={() => refetchSummary()}
        />
      )}

      {/* Summary Cards */}
      {!summaryLoading && summary.length > 0 && (
        <div className="grid md:grid-cols-3 gap-4">
          {summary.map((item) => {
            const tab = tabs.find((t) => t.id === item.tokenType)
            const Icon = tab?.icon || Coins
            return (
              <div
                key={item.tokenType}
                className={cn(
                  'card p-4 cursor-pointer transition-all',
                  activeTab === item.tokenType
                    ? 'border-uni-500/50 bg-uni-500/5'
                    : 'hover:border-dark-700'
                )}
                onClick={() => setActiveTab(item.tokenType)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-uni-500/20 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-uni-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-dark-400">{tab?.label}</p>
                    <p className="text-lg font-semibold">
                      {item.configured ? (
                        <>{item.totalHolders} holders</>
                      ) : (
                        <span className="text-dark-500 text-sm">No configurado</span>
                      )}
                    </p>
                  </div>
                  {item.isRunning && (
                    <Loader2 className="w-4 h-4 animate-spin text-uni-400" />
                  )}
                </div>
                {item.configured && (
                  <div className="mt-2 text-xs text-dark-500">
                    Total: {formatNumber(item.totalSupplyFormatted)} {tab?.symbol}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-dark-800">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.id
                  ? 'border-uni-500 text-uni-400'
                  : 'border-transparent text-dark-400 hover:text-white'
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <HoldersTable tokenType={activeTab} />
    </div>
  )
}

function HoldersTable({ tokenType }: { tokenType: TokenType }) {
  const [limit, setLimit] = useState(100)
  const [searchTerm, setSearchTerm] = useState('')
  const {
    holders,
    total,
    totalSupplyFormatted,
    lastSyncAt,
    isRunning,
    isLoading,
    syncApi,
    isSyncingApi,
  } = useTokenHolders(tokenType, { limit })

  const tab = tabs.find((t) => t.id === tokenType)
  const hasMore = holders.length < total

  // Reset limit and search when token type changes
  useEffect(() => {
    setLimit(100)
    setSearchTerm('')
  }, [tokenType])

  // Filter holders by search term (wallet or username)
  const filteredHolders = searchTerm.trim()
    ? holders.filter((holder) => {
        const search = searchTerm.toLowerCase()
        const walletMatch = holder.walletAddress.toLowerCase().includes(search)
        const usernameMatch = holder.userData?.username?.toLowerCase().includes(search)
        const nameMatch = holder.userData?.name?.toLowerCase().includes(search)
        return walletMatch || usernameMatch || nameMatch
      })
    : holders

  const loadMore = () => {
    setLimit((prev) => prev + 100)
  }

  // Export holders to CSV
  const exportToCSV = () => {
    if (holders.length === 0) return

    const totalSupply = parseFloat(totalSupplyFormatted) || 0

    // CSV headers
    const headers = ['#', 'Wallet', 'Username', 'Nombre', 'Email', 'VIP', 'Balance', '%']

    // CSV rows
    const rows = holders.map((holder, index) => {
      const percentage = totalSupply > 0
        ? ((parseFloat(holder.balanceFormatted) / totalSupply) * 100).toFixed(4)
        : '0'

      return [
        index + 1,
        holder.walletAddress,
        holder.userData?.username || '',
        holder.userData?.name || '',
        holder.userData?.email || '',
        holder.userData?.isVip ? 'Sí' : 'No',
        holder.balanceFormatted,
        percentage,
      ]
    })

    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => {
          // Escape quotes and wrap in quotes if contains comma
          const str = String(cell)
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`
          }
          return str
        }).join(',')
      ),
    ].join('\n')

    // Create and download file
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `holders_${tokenType}_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  if (isLoading && holders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-uni-500" />
      </div>
    )
  }

  // Check if token is not configured (UNI2_TOKEN)
  if (tokenType === 'UNI2_TOKEN' && holders.length === 0 && !isRunning) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Token no configurado</h3>
        <p className="text-dark-400 text-sm">
          La dirección del contrato UNI2 Token no está configurada en el backend.
          <br />
          Configura UNI2_TOKEN_ADDRESS en el archivo .env del backend.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-dark-400" />
              <span className="text-sm text-dark-400">{total} holders</span>
            </div>
            <div className="text-sm text-dark-400">
              Total: <span className="text-white font-mono">{formatNumber(totalSupplyFormatted)}</span> {tab?.symbol}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastSyncAt && (
              <span className="text-xs text-dark-500">
                Última sync: {formatDate(lastSyncAt)}
              </span>
            )}
            <button
              onClick={exportToCSV}
              disabled={holders.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title="Exportar holders a CSV"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
            <button
              onClick={() => syncApi()}
              disabled={isSyncingApi || isRunning}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-uni-500/20 text-uni-400 hover:bg-uni-500/30 transition-colors text-sm"
              title="Sincronizar usando API de Wer2Scan"
            >
              <Zap className={`w-4 h-4 ${isSyncingApi || isRunning ? 'animate-pulse' : ''}`} />
              {isSyncingApi || isRunning ? 'Sincronizando...' : 'Sincronizar'}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
          <input
            type="text"
            placeholder="Buscar por wallet o usuario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 bg-dark-800 border border-dark-700 rounded-lg text-sm text-white placeholder-dark-400 focus:outline-none focus:border-uni-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="text-xs text-dark-400">
            Mostrando {filteredHolders.length} de {holders.length} holders cargados
          </div>
        )}

        {/* Sync Progress Bar */}
        {(isSyncingApi || isRunning) && (
          <div className="bg-dark-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-uni-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Sincronizando desde Wer2Scan API...
              </span>
              <span className="text-xs text-dark-400">Esto puede tardar unos segundos</span>
            </div>
            <div className="w-full h-2 bg-dark-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-uni-500 via-uni-400 to-uni-500 rounded-full animate-progress-indeterminate" />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-dark-800 text-dark-400">
              <tr>
                <th className="px-4 py-3 text-left w-12">#</th>
                <th className="px-4 py-3 text-left">Wallet</th>
                <th className="px-4 py-3 text-left">Usuario</th>
                <th className="px-4 py-3 text-right">Balance</th>
                <th className="px-4 py-3 text-right">%</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-800">
              {filteredHolders.map((holder, index) => {
                const percentage = totalSupplyFormatted && parseFloat(totalSupplyFormatted) > 0
                  ? (parseFloat(holder.balanceFormatted) / parseFloat(totalSupplyFormatted)) * 100
                  : 0

                return (
                  <tr key={holder.id} className="hover:bg-dark-800/50">
                    <td className="px-4 py-3 text-dark-500">{index + 1}</td>
                    <td className="px-4 py-3">
                      <a
                        href={`https://wer2scan.com/uni2/address/${holder.walletAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-uni-400 hover:underline"
                      >
                        {shortenAddress(holder.walletAddress, 8)}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      {holder.userData ? (
                        <div className="flex items-center gap-2">
                          <span className="text-white">
                            {holder.userData.name || holder.userData.username || '-'}
                          </span>
                          {holder.userData.isVip && (
                            <span title="VIP">
                              <Crown className="w-3.5 h-3.5 text-yellow-400" />
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-dark-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {formatNumber(holder.balanceFormatted)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 bg-dark-800 rounded-full h-1.5">
                          <div
                            className="bg-uni-500 h-1.5 rounded-full"
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                        <span className="text-dark-400 w-16 text-right">
                          {percentage.toFixed(2)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {filteredHolders.length === 0 && !isLoading && (
          <div className="p-8 text-center text-dark-400">
            {searchTerm ? (
              <>No se encontraron holders que coincidan con "<span className="text-white">{searchTerm}</span>"</>
            ) : (
              'No hay holders registrados. Ejecuta una sincronización.'
            )}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && holders.length > 0 && (
          <div className="p-4 border-t border-dark-800 flex items-center justify-center gap-3">
            <button
              onClick={loadMore}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors text-sm"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              Cargar +100
            </button>
            <button
              onClick={() => setLimit(total)}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-uni-500/20 text-uni-400 hover:bg-uni-500/30 transition-colors text-sm"
            >
              Cargar todos ({total})
            </button>
            <span className="text-xs text-dark-500">
              Mostrando {holders.length} de {total}
            </span>
          </div>
        )}
      </div>

      {/* Total Summary */}
      {holders.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <span className="text-dark-400">
              {hasMore ? `Total en ${holders.length} holders cargados:` : 'Total en todos los holders:'}
            </span>
            <div className="text-right">
              <span className="font-mono text-lg">
                {formatNumber(
                  holders.reduce((acc, h) => acc + parseFloat(h.balanceFormatted), 0).toString()
                )}{' '}
                {tab?.symbol}
              </span>
              {hasMore && (
                <p className="text-xs text-dark-500">
                  (Carga todos los holders para ver el total real)
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function formatNumber(value: string): string {
  const num = parseFloat(value)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Sync Modal Component
function SyncModal({
  summary,
  onClose,
  onSyncComplete,
}: {
  summary: any[]
  onClose: () => void
  onSyncComplete: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-800">
          <h2 className="text-lg font-semibold">Sincronizar Holders</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-dark-800 text-dark-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <p className="text-sm text-dark-400 mb-4">
            Selecciona qué token quieres sincronizar desde Wer2Scan API:
          </p>

          {tabs.map((tab) => {
            const summaryItem = summary.find((s) => s.tokenType === tab.id)
            return (
              <SyncTokenRow
                key={tab.id}
                tokenType={tab.id}
                label={tab.label}
                Icon={tab.icon}
                configured={summaryItem?.configured !== false}
                totalHolders={summaryItem?.totalHolders || 0}
                isRunning={summaryItem?.isRunning || false}
                lastSyncAt={summaryItem?.lastSyncAt}
                onSyncComplete={onSyncComplete}
              />
            )
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-dark-800">
          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg bg-dark-800 hover:bg-dark-700 text-white transition-colors text-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

// Individual token sync row
function SyncTokenRow({
  tokenType,
  label,
  Icon,
  configured,
  totalHolders,
  isRunning: isRunningProp,
  lastSyncAt,
  onSyncComplete,
}: {
  tokenType: TokenType
  label: string
  Icon: any
  configured: boolean
  totalHolders: number
  isRunning: boolean
  lastSyncAt: string | null
  onSyncComplete: () => void
}) {
  const { sync, isSyncing } = useSyncTokenHolders(tokenType)
  const isBusy = isSyncing || isRunningProp

  const handleSync = () => {
    sync()
    setTimeout(() => onSyncComplete(), 3000)
  }

  return (
    <div className="flex items-center gap-4 p-4 bg-dark-800/50 rounded-xl border border-dark-700">
      <div className="w-10 h-10 bg-uni-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-uni-400" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-white">{label}</p>
        {configured ? (
          <p className="text-xs text-dark-400">
            {totalHolders} holders
            {lastSyncAt && (
              <span className="ml-2">• Última sync: {formatDate(lastSyncAt)}</span>
            )}
          </p>
        ) : (
          <p className="text-xs text-yellow-500">No configurado</p>
        )}
      </div>

      <button
        onClick={handleSync}
        disabled={isBusy || !configured}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
          isBusy
            ? 'bg-uni-500/20 text-uni-400'
            : configured
              ? 'bg-uni-500 text-dark-950 hover:bg-uni-400'
              : 'bg-dark-700 text-dark-500 cursor-not-allowed'
        )}
      >
        {isBusy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sincronizando...
          </>
        ) : (
          <>
            <Zap className="w-4 h-4" />
            Sincronizar
          </>
        )}
      </button>
    </div>
  )
}
